import React, { useState, useEffect } from 'react';
import {
  Users,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Plus,
  Shield,
  UserCheck,
  Search,
  Key,
  RefreshCw,
  Copy,
  Check,
  Eye,
  EyeOff,
  Mail,
  MailCheck,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserPlus,
  Building2,
  Edit2,
  MessageSquare,
  Video,
  Layers,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import { useUserProfileModal } from '../../context/UserProfileModalContext.tsx';
import { useCall } from '../../context/CallContext.tsx';
import { UserWorkload, Department, UserRole, User } from '../../../shared/types.ts';
import { Avatar } from '../common/Avatar.tsx';
import { RoleBadge } from '../common/Badge.tsx';
import { Modal } from '../common/Modal.tsx';
import { InviteModal } from './InviteModal.tsx';

// Helper to generate a strong random password
const generateSecurePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%&*';
  let pass = 'Pulse-';
  for (let i = 0; i < 8; i++) {
    pass += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pass;
};

export const TeamWorkloadView: React.FC = () => {
  const { token, user } = useAuth();
  const { onlineUserIds, subscribe } = useRealtime();
  const { openUserProfile, navigateToChatWithUser } = useUserProfileModal();
  const { joinCall } = useCall();
  const isAdmin = user?.role === 'admin';
  const [workloads, setWorkloads] = useState<UserWorkload[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDept, setSelectedDept] = useState<string>('all');
  const [search, setSearch] = useState('');

  const isUserOnline = (userId?: string | null): boolean => {
    if (!userId || !onlineUserIds) return false;
    if (typeof (onlineUserIds as any).has === 'function') {
      return (onlineUserIds as Set<string>).has(userId);
    }
    if (Array.isArray(onlineUserIds)) {
      return (onlineUserIds as string[]).includes(userId);
    }
    return false;
  };

  // Modals
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState<UserWorkload | null>(null);
  const [memberToDelete, setMemberToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Department Management state (Admin only)
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [deptName, setDeptName] = useState('');
  const [deptDescription, setDeptDescription] = useState('');
  const [deptLeadUserId, setDeptLeadUserId] = useState('');
  const [isDeptSubmitting, setIsDeptSubmitting] = useState(false);
  const [deptError, setDeptError] = useState<string | null>(null);

  // New User Form state (Admin only)
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(generateSecurePassword);
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState<UserRole>('member');
  const [departmentId, setDepartmentId] = useState('');
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Created User Credentials Result Dialog
  const [createdResult, setCreatedResult] = useState<{
    user: any;
    generatedPassword: string;
    verificationCode: string;
    emailDelivered?: boolean;
    message?: string;
  } | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const getAuthHeaders = (extra: Record<string, string> = {}) => ({
    Authorization: `Bearer ${token || ''}`,
    ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
    ...extra,
  });

  const fetchWorkload = async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/team/workload', {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setWorkloads(data.workloads);
      }
    } catch (err) {
      console.error('Failed to load workload:', err);
    }
  };

  const isDummyDept = (name?: string | null): boolean => {
    if (!name) return false;
    const lower = name.toLowerCase().trim();
    return (
      lower.includes('insuretech') ||
      lower.includes('insurance solutions') ||
      lower.includes('regulatory compliance') ||
      lower.includes('cloud infrastructure')
    );
  };

  const fetchDepartments = async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/team/departments', {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const validDepts = (data.departments || []).filter(
          (d: any) => !isDummyDept(d.name)
        );
        setDepartments(validDepts);
      }
    } catch (err) {
      console.error('Failed to load departments:', err);
    }
  };

  const handleSaveDepartment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !deptName.trim()) return;
    setIsDeptSubmitting(true);
    setDeptError(null);

    try {
      const url = editingDept ? `/api/team/departments/${editingDept.id}` : '/api/team/departments';
      const method = editingDept ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: deptName.trim(),
          description: deptDescription.trim() || null,
          leadUserId: deptLeadUserId || null,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to save department.');
      }

      setDeptName('');
      setDeptDescription('');
      setDeptLeadUserId('');
      setEditingDept(null);
      await fetchDepartments();
      await fetchWorkload();
    } catch (err: any) {
      setDeptError(err.message || 'An error occurred.');
    } finally {
      setIsDeptSubmitting(false);
    }
  };

  const handleDeleteDepartment = async (deptId: string) => {
    if (!token || !confirm('Are you sure you want to delete this department? Members in this department will be unassigned.')) return;
    try {
      const res = await fetch(`/api/team/departments/${deptId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        if (selectedDept === deptId) setSelectedDept('all');
        await fetchDepartments();
        await fetchWorkload();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to delete department.');
      }
    } catch (err) {
      console.error('Error deleting department:', err);
    }
  };

  useEffect(() => {
    fetchWorkload();
    fetchDepartments();
  }, [token]);

  // Real-time synchronization for team workload and task metrics
  useEffect(() => {
    const handleTaskMutated = () => {
      fetchWorkload();
    };

    window.addEventListener('task-created', handleTaskMutated);
    window.addEventListener('task-updated', handleTaskMutated);
    window.addEventListener('task-archived', handleTaskMutated);
    window.addEventListener('task-restored', handleTaskMutated);

    const unsubscribe = subscribe((event) => {
      if (
        event.type === 'task_created' ||
        event.type === 'task_updated' ||
        event.type === 'task_archived' ||
        event.type === 'task_restored' ||
        event.type === 'tasks_reordered' ||
        event.type === 'tasks_imported'
      ) {
        fetchWorkload();
      }
    });

    return () => {
      window.removeEventListener('task-created', handleTaskMutated);
      window.removeEventListener('task-updated', handleTaskMutated);
      window.removeEventListener('task-archived', handleTaskMutated);
      window.removeEventListener('task-restored', handleTaskMutated);
      unsubscribe();
    };
  }, [subscribe, token]);

  const handleCopy = (text: string, keyName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyName);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setIsSubmitting(true);
    setCreateError(null);

    try {
      const res = await fetch('/api/team/members', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          password,
          role,
          departmentId: departmentId || null,
          title: title.trim(),
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setIsAddUserOpen(false);
        setCreatedResult({
          user: data.user,
          generatedPassword: data.generatedPassword,
          verificationCode: data.verificationCode,
          emailDelivered: data.emailDelivered,
          message: data.message,
        });
        setFullName('');
        setEmail('');
        setTitle('');
        setPassword(generateSecurePassword());
        fetchWorkload();
      } else {
        setCreateError(data.error || 'Failed to create user account.');
      }
    } catch (err: any) {
      setCreateError(err.message || 'Failed to communicate with user service.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyUserManually = async (userId: string) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/team/members/${userId}/verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchWorkload();
      }
    } catch (err) {
      console.error('Failed to manually verify user:', err);
    }
  };

  const handleResendCodeManually = async (userId: string) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/team/members/${userId}/resend-code`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchWorkload();
      }
    } catch (err) {
      console.error('Failed to regenerate verification code:', err);
    }
  };

  const handleResendCredentials = async (member: User) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/team/members/${member.id}/resend-credentials`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setCreatedResult({
          user: member,
          generatedPassword: data.generatedPassword,
          verificationCode: data.verificationCode,
          emailDelivered: data.emailDelivered,
          message: data.message,
        });
        fetchWorkload();
      }
    } catch (err) {
      console.error('Failed to regenerate & email credentials:', err);
    }
  };

  const handleUpdateRole = async (newRole: UserRole) => {
    if (!token || !selectedUserForRole) return;

    try {
      const res = await fetch(`/api/team/members/${selectedUserForRole.user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (res.ok) {
        setSelectedUserForRole(null);
        fetchWorkload();
      } else {
        const body = await res.json().catch(() => ({}));
        alert(body.error || `Could not change the role (HTTP ${res.status}).`);
      }
    } catch (err) {
      console.error('Failed to update role:', err);
      alert('Could not change the role: the server could not be reached.');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!token) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/team/members/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setDeleteError(data.error || 'Failed to remove user.');
        return;
      }
      setMemberToDelete(null);
      setSelectedUserForRole(null);
      fetchWorkload();
    } catch (err: any) {
      console.error('Failed to delete member:', err);
      setDeleteError(err.message || 'Network error.');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredWorkloads = workloads.filter((w) => {
    if (selectedDept !== 'all' && w.user.departmentId !== selectedDept) return false;
    if (search.trim()) {
      const term = search.toLowerCase();
      return (
        w.user.fullName.toLowerCase().includes(term) ||
        w.user.email.toLowerCase().includes(term) ||
        (w.user.title && w.user.title.toLowerCase().includes(term))
      );
    }
    return true;
  });

  const canManage = user?.role === 'admin' || user?.role === 'manager';

  const getCapacityColor = (status: string) => {
    switch (status) {
      case 'overloaded':
        return 'bg-rose-500 text-rose-500';
      case 'high':
        return 'bg-amber-500 text-amber-500';
      case 'balanced':
        return 'bg-emerald-500 text-emerald-500';
      default:
        return 'bg-blue-500 text-blue-500';
    }
  };

  return (
    <div className="space-y-4">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-2xs">
        <h2 className="text-sm sm:text-base font-extrabold text-surface-900 dark:text-surface-100 tracking-tight uppercase">
          Team Directory & Workload Capacity
        </h2>

        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setIsInviteOpen(true)}
            className="px-3 py-1.5 bg-[#121417] hover:bg-black text-white rounded-xl text-[11px] font-bold shadow-sm flex items-center space-x-1 transition-colors"
            title="Invite new members via email"
          >
            <UserPlus className="w-3.5 h-3.5 text-brand-400" />
            <span>Invite People</span>
          </button>

          {isAdmin && (
            <>
              <button
                onClick={() => {
                  setDeptError(null);
                  setEditingDept(null);
                  setDeptName('');
                  setDeptDescription('');
                  setDeptLeadUserId('');
                  setIsDeptModalOpen(true);
                }}
                className="px-3 py-1.5 bg-white dark:bg-surface-800 hover:bg-surface-50 dark:hover:bg-surface-700 text-surface-800 dark:text-surface-200 border border-surface-200 dark:border-surface-700 rounded-xl text-[11px] font-bold shadow-sm flex items-center space-x-1 transition-colors"
                title="Manage Company Departments"
              >
                <Layers className="w-3.5 h-3.5 text-brand-500" />
                <span>Departments ({departments.length})</span>
              </button>

              <button
                onClick={() => {
                  setPassword(generateSecurePassword());
                  setCreateError(null);
                  setIsAddUserOpen(true);
                }}
                className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-[11px] font-bold shadow-sm flex items-center space-x-1 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Create User</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="px-3.5 py-2 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-2xs flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search team members..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>

        <div className="flex items-center space-x-2">
          {departments.length > 0 && (
            <select
              value={selectedDept}
              onChange={(e) => setSelectedDept(e.target.value)}
              aria-label="Filter by Department"
              className="text-xs px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 focus:outline-none"
            >
              <option value="all">All Departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          )}

          {isAdmin && (
            <button
              onClick={() => {
                setDeptError(null);
                setEditingDept(null);
                setDeptName('');
                setDeptDescription('');
                setDeptLeadUserId('');
                setIsDeptModalOpen(true);
              }}
              className="text-xs px-2.5 py-1.5 text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950/40 rounded-xl font-semibold flex items-center space-x-1 transition-colors"
              title="Add or Manage Departments"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Dept</span>
            </button>
          )}
        </div>
      </div>

      {/* Workload Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredWorkloads.map((wl) => {
          const isSelf = user?.id === wl.user.id;
          const sanitizedDept =
            wl.user.departmentName &&
            !['insuretech engineering', 'insurance solutions & architecture', 'regulatory compliance & qa', 'cloud infrastructure & cybersecurity'].includes(
              wl.user.departmentName.toLowerCase()
            )
              ? wl.user.departmentName
              : 'General';

          return (
            <div
              key={wl.user.id}
              onClick={() => openUserProfile(wl.user)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  openUserProfile(wl.user);
                }
              }}
              className="bg-white dark:bg-surface-900 rounded-2xl p-5 border border-surface-200 dark:border-surface-800 shadow-card hover:shadow-elevated hover:border-brand-400 dark:hover:border-brand-500 transition-all flex flex-col justify-between cursor-pointer group focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            >
              <div>
                {/* Member Profile Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3 min-w-0">
                    <Avatar
                      src={wl.user.avatarUrl}
                      name={wl.user.fullName}
                      size="md"
                      isOnline={isUserOnline(wl.user.id)}
                      userData={wl.user}
                      userId={wl.user.id}
                    />
                    <div className="min-w-0">
                      <div className="flex items-center space-x-1.5 flex-wrap gap-y-1">
                        <h3 className="font-bold text-sm text-surface-900 dark:text-surface-100 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors truncate">
                          {wl.user.fullName}
                        </h3>
                        {isUserOnline(wl.user.id) ? (
                          <span className="px-1.5 py-0.2 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-[9px] font-bold flex items-center space-x-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            <span>Online</span>
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.2 rounded-md bg-surface-500/10 text-surface-500 dark:text-surface-400 border border-surface-500/20 text-[9px] font-medium flex items-center space-x-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-surface-400" />
                            <span>Offline</span>
                          </span>
                        )}
                        {wl.user.isVerified === false || (wl.user.isVerified as any) === 0 ? (
                          <span className="px-1.5 py-0.2 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-[9px] font-bold">
                            Pending Verification
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.2 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-[9px] font-semibold flex items-center space-x-0.5">
                            <Check className="w-2.5 h-2.5 inline" />
                            <span>Verified</span>
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-surface-500 truncate">{wl.user.title || wl.user.email}</p>

                      {/* Admin Verification Code Viewer & Quick Verify */}
                      {isAdmin && (wl.user.isVerified === false || (wl.user.isVerified as any) === 0) && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pt-1 border-t border-dashed border-amber-500/30">
                          {wl.user.verificationCode && (
                            <div className="flex items-center space-x-1 bg-amber-500/15 border border-amber-500/30 rounded-lg px-2 py-0.5">
                              <Key className="w-3 h-3 text-amber-600" />
                              <span className="font-mono text-[10px] font-bold text-amber-800 dark:text-amber-200">
                                {wl.user.verificationCode}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopy(wl.user.verificationCode!, `code-${wl.user.id}`);
                                }}
                                title="Copy verification code"
                                className="text-amber-700 dark:text-amber-300 hover:text-black ml-1"
                              >
                                {copiedKey === `code-${wl.user.id}` ? (
                                  <Check className="w-3 h-3 text-emerald-600" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </button>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVerifyUserManually(wl.user.id);
                            }}
                            className="px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] font-bold shadow-xs transition-colors"
                          >
                            Verify Now
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResendCodeManually(wl.user.id);
                            }}
                            className="text-[10px] text-surface-500 hover:text-surface-800 dark:hover:text-surface-200 underline"
                          >
                            New Code
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end space-y-1.5 shrink-0 ml-2">
                    <div className="flex items-center space-x-1.5">
                      {!isSelf && (
                        <>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToChatWithUser(wl.user);
                            }}
                            title={`Send direct message to ${wl.user.fullName}`}
                            className="p-1.5 rounded-lg text-surface-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950/50 transition-colors"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              joinCall({
                                title: `Call with ${wl.user.fullName}`,
                                type: 'direct',
                              });
                            }}
                            title={`Start video call with ${wl.user.fullName}`}
                            className="p-1.5 rounded-lg text-surface-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 transition-colors"
                          >
                            <Video className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      <RoleBadge role={wl.user.role} />
                    </div>

                    {isAdmin && wl.user.id !== user?.id && (
                      <div className="flex items-center space-x-1.5 pt-0.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUserForRole(wl);
                          }}
                          className="text-[10px] text-brand-600 dark:text-brand-400 hover:underline font-semibold"
                        >
                          Change role
                        </button>
                        <span className="text-surface-300 dark:text-surface-600 text-[10px]">•</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResendCredentials(wl.user);
                          }}
                          className="text-[10px] text-indigo-600 dark:text-indigo-400 hover:underline font-semibold flex items-center space-x-0.5"
                          title="Regenerate temporary password & email credentials to user"
                        >
                          <Mail className="w-2.5 h-2.5 inline mr-0.5" />
                          <span>Email creds</span>
                        </button>
                        <span className="text-surface-300 dark:text-surface-600 text-[10px]">•</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteError(null);
                            setMemberToDelete(wl.user);
                          }}
                          className="text-[10px] text-rose-500 hover:text-rose-700 dark:hover:text-rose-400 font-semibold flex items-center space-x-0.5 transition-colors"
                          title="Remove user from workspace"
                        >
                          <Trash2 className="w-2.5 h-2.5 inline" />
                          <span>Remove</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Department & Capacity Status */}
                <div className="flex items-center justify-between text-xs py-2 px-3 rounded-xl bg-surface-50 dark:bg-surface-800/60 mb-4">
                  <span className="text-surface-500 font-medium">{sanitizedDept}</span>
                  <span className="flex items-center font-bold uppercase tracking-wider text-[10px]">
                    <span className={`w-2 h-2 rounded-full mr-1.5 ${getCapacityColor(wl.capacityStatus).split(' ')[0]}`} />
                    {wl.capacityStatus}
                  </span>
                </div>

                {/* Workload Meter */}
                <div className="space-y-1 mb-4">
                  <div className="flex justify-between text-xs">
                    <span className="text-surface-500">Workload Score</span>
                    <span className="font-bold text-surface-900 dark:text-surface-100">
                      {wl.workloadScore}%
                    </span>
                  </div>
                  <div className="w-full bg-surface-100 dark:bg-surface-800 h-2 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${getCapacityColor(wl.capacityStatus).split(' ')[0]}`}
                      style={{ width: `${wl.workloadScore}%` }}
                    />
                  </div>
                </div>

                {/* Breakdown Grid */}
                <div className="grid grid-cols-3 gap-2 text-center text-xs pt-3 border-t border-surface-100 dark:border-surface-800">
                  <div className="p-2 rounded-xl bg-surface-50 dark:bg-surface-800/40">
                    <span className="text-[10px] text-surface-400 block font-semibold">Active Tasks</span>
                    <span className="font-bold text-sm text-surface-900 dark:text-surface-100">
                      {wl.tasksByStatus.todo + wl.tasksByStatus.in_progress + wl.tasksByStatus.in_review}
                    </span>
                  </div>

                  <div className="p-2 rounded-xl bg-surface-50 dark:bg-surface-800/40">
                    <span className="text-[10px] text-surface-400 block font-semibold">Completed</span>
                    <span className="font-bold text-sm text-emerald-600 dark:text-emerald-400">
                      {wl.tasksByStatus.done}
                    </span>
                  </div>

                  <div className="p-2 rounded-xl bg-surface-50 dark:bg-surface-800/40">
                    <span className="text-[10px] text-surface-400 block font-semibold">Overdue</span>
                    <span className={`font-bold text-sm ${wl.overdueTasksCount > 0 ? 'text-rose-600' : 'text-surface-400'}`}>
                      {wl.overdueTasksCount}
                    </span>
                  </div>
                </div>
              </div>

              {/* Click cue footer */}
              <div className="mt-3 pt-2 border-t border-surface-100 dark:border-surface-800/60 flex items-center justify-between text-[11px] text-surface-400 dark:text-surface-500 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                <span>View profile, call & tasks</span>
                <span className="font-bold">→</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add User Modal (Admin Only) */}
      <Modal
        isOpen={isAddUserOpen}
        onClose={() => setIsAddUserOpen(false)}
        title="Create New User & Generate Credentials"
        maxWidth="md"
      >
        <form onSubmit={handleCreateUser} className="space-y-4 text-xs">
          <div className="p-3 bg-brand-50/60 dark:bg-brand-950/40 rounded-xl border border-brand-500/20 text-brand-900 dark:text-brand-200 text-[11px] leading-relaxed flex items-start space-x-2">
            <ShieldCheck className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5" />
            <span>
              <strong>Admin Provisioning:</strong> Provide company email and generate initial credentials. New accounts require email verification code confirmation before users can sign in.
            </span>
          </div>

          {createError && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-400 flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{createError}</span>
            </div>
          )}

          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Full Name <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Jordan Miller"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Company Email Address <span className="text-rose-500">*</span>
            </label>
            <input
              type="email"
              required
              placeholder="jordan.miller@ihbdigital.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          {/* Password Generator Field */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="font-semibold text-surface-700 dark:text-surface-300">
                Generated Password <span className="text-rose-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setPassword(generateSecurePassword())}
                className="text-brand-600 dark:text-brand-400 hover:underline flex items-center space-x-1 text-[11px]"
              >
                <RefreshCw className="w-3 h-3" />
                <span>Regenerate Password</span>
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <div className="relative flex-1">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full font-mono font-bold pr-10 pl-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 dark:hover:text-surface-200"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <button
                type="button"
                onClick={() => handleCopy(password, 'form-pass')}
                className="px-3 py-2 bg-surface-100 dark:bg-surface-800 hover:bg-surface-200 dark:hover:bg-surface-700 rounded-xl text-surface-700 dark:text-surface-300 font-semibold flex items-center space-x-1 border border-surface-200 dark:border-surface-700 shrink-0"
              >
                {copiedKey === 'form-pass' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedKey === 'form-pass' ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
            <p className="text-[10px] text-surface-400 mt-1">
              Secure initial password generated. The user can sign in with this after email verification.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Role (POV Tier)
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              >
                <option value="member">User (Member)</option>
                <option value="admin">Administrator</option>
                <option value="manager">Manager</option>
                <option value="team_lead">Team Lead</option>
                <option value="sqa">SQA</option>
              </select>
            </div>

            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Assigned Space / Team
              </label>
              <select
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              >
                <option value="">No Space Assigned (General)</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Job Title
            </label>
            <input
              type="text"
              placeholder="e.g. Lead Product Architect"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
            />
          </div>

          <div className="pt-4 border-t border-surface-100 dark:border-surface-800 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsAddUserOpen(false)}
              className="px-4 py-2 font-medium text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 font-bold bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-sm disabled:opacity-50 flex items-center space-x-1.5"
            >
              <Key className="w-3.5 h-3.5" />
              <span>{isSubmitting ? 'Creating...' : 'Create User & Generate Code'}</span>
            </button>
          </div>
        </form>
      </Modal>

      {/* Created User Credentials & Verification Code Modal */}
      {createdResult && (
        <Modal
          isOpen={true}
          onClose={() => setCreatedResult(null)}
          title="Account Created & Credentials Generated"
          maxWidth="md"
        >
          <div className="space-y-4 text-xs">
            {/* Email Delivery / Success Alert */}
            {createdResult.emailDelivered ? (
              <div className="p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 flex items-center space-x-2.5 text-emerald-800 dark:text-emerald-300">
                <MailCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                <div>
                  <p className="font-bold text-sm">Account Created & Credentials Emailed</p>
                  <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400">
                    A welcome email with login credentials and 6-digit verification code has been dispatched to <strong>{createdResult.user.email}</strong>.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center space-x-2.5 text-amber-800 dark:text-amber-300">
                <CheckCircle2 className="w-5 h-5 text-amber-600 shrink-0" />
                <div>
                  <p className="font-bold text-sm">Account Created for {createdResult.user.fullName}</p>
                  <p className="text-[11px] text-amber-700/80 dark:text-amber-400">
                    Credentials generated below. To deliver real automated emails to inboxes, set <code>SMTP_HOST</code> and <code>SMTP_USER</code> in <code>.env</code>.
                  </p>
                </div>
              </div>
            )}

            {/* Credentials Card */}
            <div className="p-4 rounded-2xl bg-surface-50 dark:bg-surface-800/60 border border-surface-200 dark:border-surface-700 space-y-3">
              <div>
                <span className="block text-[10px] uppercase font-bold text-surface-400">Company Email</span>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="font-semibold text-surface-900 dark:text-surface-100">{createdResult.user.email}</span>
                  <button
                    onClick={() => handleCopy(createdResult.user.email, 'res-email')}
                    className="text-brand-600 dark:text-brand-400 hover:underline flex items-center space-x-1"
                  >
                    {copiedKey === 'res-email' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedKey === 'res-email' ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
              </div>

              <div className="pt-2 border-t border-surface-200/50 dark:border-surface-700/50">
                <span className="block text-[10px] uppercase font-bold text-surface-400">Generated Password</span>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="font-mono font-bold text-surface-900 dark:text-surface-100 bg-white dark:bg-surface-900 px-2 py-0.5 rounded border border-surface-200 dark:border-surface-700">
                    {createdResult.generatedPassword}
                  </span>
                  <button
                    onClick={() => handleCopy(createdResult.generatedPassword, 'res-pass')}
                    className="text-brand-600 dark:text-brand-400 hover:underline flex items-center space-x-1"
                  >
                    {copiedKey === 'res-pass' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedKey === 'res-pass' ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
              </div>

              <div className="pt-2 border-t border-surface-200/50 dark:border-surface-700/50">
                <span className="block text-[10px] uppercase font-bold text-amber-600 dark:text-amber-400 flex items-center space-x-1">
                  <MailCheck className="w-3.5 h-3.5" />
                  <span>Email Verification Code (Required to Sign In)</span>
                </span>
                <div className="flex items-center justify-between mt-1">
                  <div className="px-3 py-1 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-900 dark:text-amber-200 font-mono text-base font-black tracking-widest">
                    {createdResult.verificationCode}
                  </div>
                  <button
                    onClick={() => handleCopy(createdResult.verificationCode, 'res-code')}
                    className="text-amber-700 dark:text-amber-300 hover:underline flex items-center space-x-1 font-bold"
                  >
                    {copiedKey === 'res-code' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedKey === 'res-code' ? 'Copied' : 'Copy Code'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Instructions Callout */}
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-300 text-[11px] leading-relaxed">
              <strong>Security Protocol:</strong> The user cannot sign in until this 6-digit verification code is entered on their first sign-in attempt. Please provide both the password and this code to the employee.
            </div>

            {/* Actions */}
            <div className="pt-3 border-t border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  const payload = `IHB Pulse - Employee Credentials:\nEmail: ${createdResult.user.email}\nPassword: ${createdResult.generatedPassword}\nVerification Code: ${createdResult.verificationCode}\nURL: http://localhost:5173`;
                  handleCopy(payload, 'all-info');
                }}
                className="px-4 py-2 bg-surface-100 dark:bg-surface-800 hover:bg-surface-200 dark:hover:bg-surface-700 text-surface-800 dark:text-surface-200 rounded-xl font-bold flex items-center space-x-1.5 transition-colors"
              >
                {copiedKey === 'all-info' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                <span>{copiedKey === 'all-info' ? 'Copied Full Details!' : 'Copy All Login Info'}</span>
              </button>

              <button
                type="button"
                onClick={() => setCreatedResult(null)}
                className="px-5 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-xl font-bold shadow-sm"
              >
                Done
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Change Role Modal */}
      {selectedUserForRole && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedUserForRole(null)}
          title={`Update Role: ${selectedUserForRole.user.fullName}`}
          maxWidth="sm"
        >
          <div className="space-y-3 text-xs">
            <p className="text-surface-600 dark:text-surface-400">
              Select the permission tier for this employee:
            </p>
            {(['admin', 'manager', 'team_lead', 'member', 'sqa'] as UserRole[]).map((r) => (
              <button
                key={r}
                onClick={() => handleUpdateRole(r)}
                className={`w-full text-left p-3 rounded-xl border flex items-center justify-between transition-colors ${
                  selectedUserForRole.user.role === r
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/40 text-brand-700 dark:text-brand-300 font-bold'
                    : 'border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800'
                }`}
              >
                <div>
                  <span className={r === 'sqa' ? '' : 'capitalize'}>
                    {r === 'sqa' ? 'SQA' : r.replace('_', ' ')}
                  </span>
                </div>
                {selectedUserForRole.user.role === r && <CheckCircle2 className="w-4 h-4 text-brand-600" />}
              </button>
            ))}

            {isAdmin && selectedUserForRole.user.id !== user?.id && (
              <div className="pt-3 mt-2 border-t border-surface-200 dark:border-surface-800">
                <button
                  type="button"
                  onClick={() => {
                    const u = selectedUserForRole.user;
                    setSelectedUserForRole(null);
                    setDeleteError(null);
                    setMemberToDelete(u);
                  }}
                  className="w-full py-2.5 px-3 rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/30 dark:hover:bg-rose-950/50 text-rose-600 dark:text-rose-400 font-bold text-xs flex items-center justify-center space-x-1.5 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Remove User from Workspace</span>
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Delete User Confirmation Modal */}
      {memberToDelete && (
        <Modal
          isOpen={!!memberToDelete}
          onClose={() => {
            if (!isDeleting) setMemberToDelete(null);
          }}
          title="Remove Team Member"
          maxWidth="md"
        >
          <div className="space-y-4 text-xs font-sans">
            <div className="flex items-start space-x-3 p-3.5 bg-rose-50 dark:bg-rose-950/30 rounded-2xl border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300">
              <AlertTriangle className="w-5 h-5 shrink-0 text-rose-600 mt-0.5" />
              <div>
                <p className="font-bold text-sm text-rose-800 dark:text-rose-200 mb-1">
                  Permanently remove this user?
                </p>
                <p className="leading-relaxed">
                  Are you sure you want to remove <strong>{memberToDelete.fullName}</strong> ({memberToDelete.email}) from IHB Pulse?
                </p>
              </div>
            </div>

            <p className="text-surface-500 dark:text-surface-400 leading-relaxed">
              They will immediately lose access to the system. Any assigned tasks will be unassigned, and their project memberships will be revoked.
            </p>

            {deleteError && (
              <div className="p-2.5 bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 rounded-xl font-medium">
                {deleteError}
              </div>
            )}

            <div className="flex items-center justify-end space-x-2 pt-3 border-t border-surface-200 dark:border-surface-800">
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setMemberToDelete(null)}
                className="px-4 py-2 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-300 font-bold transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => handleDeleteUser(memberToDelete.id)}
                className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold flex items-center space-x-1.5 shadow-sm transition-colors disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Removing...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Remove User</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Admin Department Management Modal */}
      {isDeptModalOpen && (
        <Modal
          isOpen={isDeptModalOpen}
          onClose={() => {
            setIsDeptModalOpen(false);
            setEditingDept(null);
          }}
          title={editingDept ? 'Edit Department' : 'Manage Company Departments'}
          maxWidth="lg"
        >
          <div className="space-y-5 text-xs font-sans">
            {/* Department Context Banner */}
            <div className="p-3.5 bg-brand-50/80 dark:bg-brand-950/40 border border-brand-200 dark:border-brand-800/60 rounded-2xl flex items-start space-x-3">
              <Layers className="w-5 h-5 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5" />
              <div className="text-[11px] leading-relaxed text-surface-700 dark:text-surface-300">
                <span className="font-bold text-surface-900 dark:text-surface-100">Company Departments:</span>{' '}
                Create and manage functional departments to categorize your team members (e.g. Engineering, Sales, Marketing, Customer Support). Project execution workspaces and Kanban boards are organized separately under Teams in the navigation.
              </div>
            </div>

            {/* Department Create / Edit Form */}
            <form onSubmit={handleSaveDepartment} className="p-4 bg-surface-50 dark:bg-surface-800/60 rounded-2xl border border-surface-200 dark:border-surface-700 space-y-3.5">
              <h4 className="font-bold text-surface-900 dark:text-surface-100 flex items-center space-x-1.5">
                <Layers className="w-4 h-4 text-brand-500" />
                <span>{editingDept ? 'Edit Department Details' : 'Create New Department'}</span>
              </h4>

              {deptError && (
                <div className="p-2.5 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-xl text-rose-700 dark:text-rose-300">
                  {deptError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                    Department Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Engineering, Product, Marketing"
                    value={deptName}
                    onChange={(e) => setDeptName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                    Department Lead (Optional)
                  </label>
                  <select
                    value={deptLeadUserId}
                    onChange={(e) => setDeptLeadUserId(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  >
                    <option value="">No Lead Assigned</option>
                    {workloads.map((wl) => (
                      <option key={wl.user.id} value={wl.user.id}>
                        {wl.user.fullName} ({wl.user.email})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  placeholder="Brief description of department scope and team responsibilities"
                  value={deptDescription}
                  onChange={(e) => setDeptDescription(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>

              <div className="flex items-center justify-end space-x-2 pt-1">
                {editingDept && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingDept(null);
                      setDeptName('');
                      setDeptDescription('');
                      setDeptLeadUserId('');
                    }}
                    className="px-3 py-1.5 rounded-xl text-surface-600 hover:bg-surface-200 dark:hover:bg-surface-700 font-semibold"
                  >
                    Cancel Edit
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isDeptSubmitting || !deptName.trim()}
                  className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold disabled:opacity-50 flex items-center space-x-1.5 transition-colors shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>{editingDept ? 'Update Department' : 'Create Department'}</span>
                </button>
              </div>
            </form>

            {/* Department List */}
            <div>
              <h5 className="font-bold text-surface-900 dark:text-surface-100 mb-2">
                Company Departments ({departments.length})
              </h5>

              {departments.length === 0 ? (
                <div className="p-6 text-center border-2 border-dashed border-surface-200 dark:border-surface-700 rounded-2xl text-surface-500">
                  <Layers className="w-8 h-8 mx-auto text-surface-400 mb-2 opacity-60" />
                  <p className="font-semibold text-surface-700 dark:text-surface-300">No departments created yet.</p>
                  <p className="text-[11px] mt-0.5">Use the form above to add your company departments.</p>
                </div>
              ) : (
                <div className="border border-surface-200 dark:border-surface-700 rounded-2xl divide-y divide-surface-100 dark:divide-surface-800 overflow-hidden">
                  {departments.map((dept) => (
                    <div key={dept.id} className="p-3.5 flex items-center justify-between gap-3 hover:bg-surface-50 dark:hover:bg-surface-800/40 transition-colors">
                      <div className="flex items-start space-x-3 min-w-0">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 shadow-xs bg-brand-500/10 text-brand-600 dark:text-brand-400"
                        >
                          <Layers className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-surface-900 dark:text-surface-100 text-xs">
                              {dept.name}
                            </span>
                            <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-md uppercase bg-surface-100 dark:bg-surface-800 text-surface-500">
                              Department
                            </span>
                          </div>
                          {dept.description && (
                            <div className="text-[11px] text-surface-500 truncate mt-0.5">
                              {dept.description}
                            </div>
                          )}
                          <div className="text-[10px] text-surface-400 mt-0.5 flex items-center space-x-3">
                            <span>{dept.memberCount || 0} active member{dept.memberCount === 1 ? '' : 's'}</span>
                            {dept.leadName && <span>Lead: {dept.leadName}</span>}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingDept(dept);
                            setDeptName(dept.name);
                            setDeptDescription(dept.description || '');
                            setDeptLeadUserId(dept.leadUserId || '');
                            setDeptError(null);
                          }}
                          className="p-1.5 rounded-lg text-surface-500 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-950/40 transition-colors"
                          title="Edit Department"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteDepartment(dept.id)}
                          className="p-1.5 rounded-lg text-surface-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                          title="Delete Department"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* ClickUp-Style Workspace Invite Modal */}
      <InviteModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        onInviteSuccess={() => {
          fetchWorkload();
        }}
      />
    </div>
  );
};
